
//import { AssignmentPlanned, defaultTaskValues, useCustomAssignments, useLinks, useOverrides } from '@/lib/customassignments';
//import { LinkType } from '@/lib/linking';
import { addDays, addMinutes, differenceInCalendarDays, format, isBefore, isSameDay } from 'date-fns';
import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { useCalendarEvents } from "../../../../hooks/useCalendarEvents.ts";
import { useCourses } from '@/lib/courses';
import { useCommunication } from '@/lib/CommunicationWrapper';
import { mapAssignmentToApi, useAssignmentSync } from '@/hooks/useAssignmentSync.ts';
import { AssignmentPlanned, defaultTaskValues } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import { useCalendarQueue } from '@/lib/CalendarQueueHelper';

import { useCustomAssignments, useOverrides, useLinks, LinkType } from '@/hooks/useCustomAssignments.ts';

const DragContext = createContext<any>(null);
const PlannerContext = createContext<any>(null);

export const useDrag = () => {
    const context = useContext(DragContext);
    if (!context) {
        throw new Error('useDrag must be used within an DragProvider');
    }
    return context;
};

export const usePlanner = () => {
    const context = useContext(PlannerContext);
    if (!context) {
        throw new Error('usePlanner must be used within an PlannerProvider');
    }
    return context;
};

export const DragProvider = ({ children }: { children: React.ReactNode }) => {
    const [currentDragDate, setCurrentDragDate] = useState<Date | null>(null);
    const [activeCalendarDragItem, setActiveCalendarDragItem] = useState(null);
    const [activeDragItem, setActiveDragItem] = useState(null);
    const [resizeHeight, setResizeHeight] = useState(null);
    const [dropZone, setDropZone] = useState(null);
    const [currentDragDelta, setCurrentDragDelta] = useState<Date | null>(null);
    const [shouldAnimateDrop, setShouldAnimateDrop] = useState(false);

    const value: any = useMemo(() => ({
        currentDragDate,
        setCurrentDragDate,
        activeDragItem,
        setActiveDragItem,
        resizeHeight,
        setResizeHeight,
        dropZone,
        setDropZone,
        currentDragDelta,
        setCurrentDragDelta,
        activeCalendarDragItem,
        setActiveCalendarDragItem,
        shouldAnimateDrop,
        setShouldAnimateDrop,
    }), [currentDragDate, activeCalendarDragItem, activeDragItem, resizeHeight, dropZone, currentDragDelta, shouldAnimateDrop]);

    return <DragContext.Provider value={value}>{children}</DragContext.Provider>;
};

export const PlannerProvider = ({ children, portal }: { children: React.ReactNode, portal: HTMLElement | null }) => {

    const date = (new Date());
    date.setHours(0, 0, 0, 0);
    const { updateOptions, useRerenderListener, sendMessageAndListen, getOptions, showError, openGate } = useCommunication();
    const { schedule: classSchedule } = useCourses();
    const { events: calendarEvents } = useCalendarEvents(new Date(), addDays(new Date(), 7));
    const { updatePlannedCache, upsertPlannedAssignment, deletePlannedAssignments, triggerCalendarPullSync, getWeeklyUsage } = useAssignmentSync();
    const { planType } = useAuth();
    const { addToQueue } = useCalendarQueue();

    const [view, _setView] = useState<string>("planner");
    const [startDate, setStartDate] = useState<Date>(date);
    const [miniCalDate, setMiniCalDate] = useState<Date>(date);
    const [miniCalStart, setMiniCalStart] = useState<Date>(date);
    const [cursorType, setCursorType] = useState<string>("default");
    const [creatorAssignment, setCreatorAssignment] = useState<any | null>(null);
    const [weeklyUsage, setWeeklyUsage] = useState<{ current_usage: number; max_limit: number; remaining: number } | null>(null);
    const [usageLoading, setUsageLoading] = useState(true);


    // initialize settings from storage
    const { options: settings, trigger } = useRerenderListener([
        "assignments_unassigned_period",
        "assignments_minical_days",
        "assignments_week_days",
        "assignments_calendar_view_type",
        "assignments_zoom",
        "assignments_view",
        "assignments_auto_plan",
        "assignments_display_off",
        "todo_24hr",
        "assignments_block_height"
    ]);
    /*
    const [settings, setSettings] = useState<any>(() => loadInitialSettings([
        "assignments_unassigned_period",
        "assignments_minical_days",
        "assignments_week_days",
        "assignments_calendar_view_type",
        "assignments_zoom",
        "assignments_view",
        "assignments_auto_plan",
        "assignments_display_off",
        "todo_24hr",
        "assignments_block_height"
    ]));
    */

    const [filters, setFilters] = useState<any>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const courseFilter = urlParams.get("filter_course");

        return {
            "course": courseFilter ? [courseFilter] : [],
            "status": [],
            "duration": [],
            "type": [],
            "linked": [],
        };
    });

    const blockHeight = Math.round(72 * ((settings.assignments_zoom || 100) / 100));
    const dueBlockWidth = 290;
    const timeWidth = 100;
    const blockMargin = 0;
    const subBlocks = 4;
    const subBlockMinutes = 60 / subBlocks;
    const subBlockHeight = blockHeight / subBlocks;
    const [unassignedStart, setUnassignedStart] = useState<Date>(new Date());
    const scrollArea = useRef<HTMLDivElement | null>(null);

    const updateSettings = async (updates: any) => {
        /*
        setSettings((prev: any) => {
            return { ...prev, ...updates };
        });
        */
        updateOptions(updates);
    }

    const getCalendarBlockWidth = () => {
        if (settings.assignments_view === "planner") {
            const calendarViewType = settings.assignments_calendar_view_type || "week";
            const numDays = calendarViewType === "5day" ? 5 : (settings.assignments_week_days || 7);
            const containerWidth = scrollArea.current?.clientWidth ?? window.innerWidth;
            const availableWidth = containerWidth - timeWidth;
            return Math.floor(availableWidth / numDays);
        }
        if (settings.assignments_minical_days === 3) return 120;
        if (settings.assignments_minical_days === 2) return 150;
        if (settings.assignments_minical_days === 1) return 220;
        return 320;
    }

    const [blockWidth, setBlockWidth] = useState<number>(getCalendarBlockWidth());


    const setMiniCalCols = (cols: number) => {
        updateSettings({ "assignments_minical_days": cols });
    }

    const setWeekDays = (days: number) => {
        updateSettings({ "assignments_week_days": days });
    }

    const setZoom = (percent: number) => {
        updateSettings({ "assignments_zoom": percent });
    }

    useEffect(() => {
        setBlockWidth(getCalendarBlockWidth());
    }, [trigger]);

    const getRowFromDelta = (delta: any) => {

        const nearestSubBlock = Math.floor(delta?.getMinutes() / (60 / subBlocks));
        const row = delta?.getHours() + (nearestSubBlock / subBlocks);

        return row;
    }

    const [scrollToHour, setScrollToHour] = useState<number | null>(null);


    const scrollPos = useRef({ "top": 0, "left": 0 });
    //const [scrollPos, setScrollPos] = useState({ "top": 0, "left": 0 });
    const calendarActions = useRef(null);

    useEffect(() => {
        if (settings.assignments_view !== "planner") return;
        const node = scrollArea.current as HTMLDivElement | null;
        if (!node) return;

        const updateWidth = (width: number) => {
            const calendarViewType = settings.assignments_calendar_view_type || "week";
            const days = calendarViewType === "5day" ? 5 : (settings.assignments_week_days || 7);
            const available = width - timeWidth;
            if (!Number.isFinite(available) || available <= 0) return;
            const next = Math.max(1, Math.floor(available / days));
            setBlockWidth((prev) => (prev === next ? prev : next));
        };

        updateWidth(node.clientWidth);

        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver((entries) => {
            const nextWidth = entries[0]?.contentRect?.width ?? node.clientWidth;
            updateWidth(nextWidth);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [settings.assignments_view, settings.assignments_calendar_view_type, settings.assignments_week_days, timeWidth]);

    // this is an updated list of all custom assignments
    const customAssignments = useCustomAssignments();
    const overrides = useOverrides();
    const links = useLinks();
    const plannedRaw = useRerenderListener(["cached_planned_assignments"], "local");

    // Use ref for the actual data to avoid spreads, but keep a version counter for triggering re-renders
    const plannedRef = useRef<any>(plannedRaw.options?.cached_planned_assignments || {});
    const [plannedVersion, setPlannedVersion] = useState(0);

    // Mutex to prevent concurrent autoScheduleTasks calls from racing
    const schedulingInProgressRef = useRef<Promise<Record<string, string>> | null>(null);

    // Create a stable getter that components can use
    const planned = plannedRef.current;

    // Update ref when the raw data changes (from storage/communication layer)
    useEffect(() => {
        const newPlanned = plannedRaw.options?.cached_planned_assignments || {};
        // Only update if the data actually changed
        if (JSON.stringify(newPlanned) !== JSON.stringify(plannedRef.current)) {
            plannedRef.current = newPlanned;
            setPlannedVersion(v => v + 1); // Trigger re-render
        }
    }, [plannedRaw.options?.cached_planned_assignments]);

    console.log("useCachedData planned", planned);

    // this is the final list of all items combined (api, custom, and overrides after filtering)
    const [dueItems, setDueItems] = useState<any[]>([]);

    // cached list of all items gathered from the api only
    const [apiCache, setApiCache] = useState<any[]>([]);

    // flag to check if the planner has been initliazed
    const initialized = useRef(false);

    // Track todo_cache updates to ensure UI updates when API data arrives
    const [todoCacheVersion, setTodoCacheVersion] = useState(0);


    const [numCols, setNumCols] = useState<number>(() => {
        const calendarViewType = settings.assignments_calendar_view_type || "week";
        const weekDays = calendarViewType === "5day" ? 5 : (settings.assignments_week_days || 7);
        return weekDays * 3;
    });

    const [loading, setLoading] = useState<boolean>(false);

    // Update numCols when week days setting changes
    useEffect(() => {
        const calendarViewType = settings.assignments_calendar_view_type || "week";
        const weekDays = calendarViewType === "5day" ? 5 : (settings.assignments_week_days || 7);
        setNumCols(weekDays * 3);
    }, [settings.assignments_calendar_view_type, settings.assignments_week_days]);

    // Fetch initial weekly usage
    useEffect(() => {
        const fetchUsage = async () => {
            try {
                const data = await getWeeklyUsage();
                setWeeklyUsage(data);
            } catch (error) {
                console.error('Failed to fetch weekly usage:', error);
            } finally {
                setUsageLoading(false);
            }
        };
        fetchUsage();
    }, [planType]);

    // Helper functions for tracking usage changes
    const incrementUsage = (amount: number = 1) => {
        if (planType !== 'pro' && weeklyUsage) {
            setWeeklyUsage(prev => prev ? {
                ...prev,
                current_usage: prev.current_usage + amount,
                remaining: Math.max(0, prev.remaining - amount)
            } : null);
        }
    };

    const decrementUsage = (amount: number = 1) => {
        if (planType !== 'pro' && weeklyUsage) {
            setWeeklyUsage(prev => prev ? {
                ...prev,
                current_usage: Math.max(0, prev.current_usage - amount),
                remaining: Math.min(prev.max_limit, prev.remaining + amount)
            } : null);
        }
    };


    // wrapper function to set the view and save it to storage
    const setView = async (view: string) => {
        _setView(view);
        updateSettings({ "assignments_view": view });
    }

    const updateCoverage = async (start: Date, end: Date) => {
        const response = await sendMessageAndListen({
            type: "CALL_EXTENSION_FUNC",
            payload: {
                func: "updateCoverage",
                args: {
                    start: start.toISOString(),
                    end: end.toISOString(),
                }
            },
        });
        console.log("updateCoverage response in iframe", response);
        setApiCache(response?.data?.todo || []);
    }


    const init = async () => {
        if (initialized.current !== false) return;
        initialized.current = true;
        await updateCoverage(new Date(), addDays(new Date(), 7));
        // setApiCache(getState("todo_cache").todo);
        _setView("due");
        setStartDate(startDate);
    }

    useEffect(() => {
        init();
    }, []);

    /*
    // Subscribe to todo_cache updates from globals.ts
    useEffect(() => {
        const handleTodoCacheUpdate = () => {
            console.log('[PlannerContext] Todo cache updated, triggering re-render');
            setTodoCacheVersion(v => v + 1);
        };

        // Subscribe to todo_cache updates
        addSubscriber('todo_cache', handleTodoCacheUpdate);

        // Cleanup subscription on unmount
        return () => {
            removeSubscriber('todo_cache', handleTodoCacheUpdate);
        };
    }, []);
    */

    /*
    // Update apiCache when todo_cache changes
    useEffect(() => {
        setApiCache(getState("todo_cache").todo);
    }, [todoCacheVersion]);
    */

    // update coverage when the start date changes
    useEffect(() => {
        const updateData = async () => {
            await updateCoverage(startDate, addDays(startDate, 7));
        };
        updateData();
    }, [startDate]);

    // update coverage when the unassigned start date changes
    useEffect(() => {
        const updateData = async () => {
            await updateCoverage(unassignedStart, addDays(unassignedStart, settings.assignments_unassigned_period));
        };
        updateData();
    }, [unassignedStart, settings.assignments_unassigned_period]);

    const filterStatus = (status: string) => {
        return filters["status"].includes(status);
    }

    const filterCourse = (course: string) => {
        return filters["course"].includes(course);
    }

    const filterDuration = (duration: number) => {
        return filters["duration"].reduce((acc: any, val: any) => {
            if (val === "short") return acc || duration <= 30;
            if (val === "medium") return acc || duration > 30 && duration <= 60;
            if (val === "long") return acc || duration > 60 && duration <= 120;
            if (val === "extended") return acc || duration > 120;
            return acc;
        }, false);
    }

    const filterDue = (due: string) => {
        const dueDate = new Date(due);
        const today = new Date();
        return filters["due"].reduce((acc: any, val: any) => {
            if (val.label === "overdue") return acc || isBefore(dueDate, today);
            if (val.label === "today") return acc || isSameDay(dueDate, today);
            if (val.label === "tomorrow") return acc || isSameDay(dueDate, addDays(today, 1));
            return acc;
        }, false);
    }

    const filterType = (type: string, courseId: string) => {
        return filters["type"].some((filterValue: string) => {
            if (filterValue.includes(':')) {
                const [filterCourseId, filterTypeName] = filterValue.split(':');
                const itemTypeName = type?.toLowerCase().replace(/\s+/g, '_');
                return filterCourseId === courseId?.toString() && filterTypeName === itemTypeName;
            }
            return filterValue === type;
        });
    }

    const filterLinked = (item: any) => {
        return filters["linked"].includes(item?.current?.numLinks > 0 ? "haslinked" : "nolinked");
    }

    const filterItems = (items: any) => {
        return items.filter((item: any) => {
            return Object.keys(filters).every((filter: string) => {
                if (filters[filter].length === 0) return true;
                if (filter === "status") return filterStatus(item?.current?.status);
                if (filter === "course") return filterCourse(item?.current?.course?.toString());
                if (filter === "duration") return filterDuration(item?.current?.duration);
                if (filter === "due") return filterDue(item?.current?.due);
                if (filter === "type") return filterType(item?.current?.type, item?.current?.course);
                if (filter === "linked") return filterLinked(item?.current?.numLinks);
            });
        });
    }

    // partial update of an assignment
    const modifyOverrides = async (id: number | string, updates: Partial<AssignmentPlanned>) => {

        const options = getOptions();
        const key = id?.toString();
        const item = options["overrides"]?.[key] || options["overrides"]?.[id as number] || {};


        const output = {
            ...item,
            ...updates,
        }

        updateOptions({
            "overrides": {
                ...options["overrides"],
                [key]: output,
            }
        });

        try {
            const result = await upsertPlannedAssignment([{ "id": id, ...updates }]);
            if (result?.usage) setWeeklyUsage(result.usage);
            await updatePlannedCache([{ "id": id, ...updates }], "update");
        } catch (error) {
            console.error("error in modifyOverrides", error);
            if (error.message === "gated") openGate("assignments_upsert");
            return;
        }

    }

    // full addition/replacement of an assignment
    const modifyPlanned = async (updates: AssignmentPlanned) => {

        console.log("modifyPlanned called with updates", updates);

        if (!updates?.id) return;
        const assignmentId = updates?.id?.toString() || "";
        const reversionData = { ...plannedRef.current[assignmentId] || {} };
        console.log("reversionData", reversionData);

        // Track usage changes: check if plannedAt is changing
        const wasPlanned = reversionData?.plannedAt != null;
        const isNowPlanned = updates?.plannedAt != null;

        // Directly mutate the ref to avoid spreading entire object
        if (!plannedRef.current[assignmentId]) {
            plannedRef.current[assignmentId] = {};
        }
        Object.assign(plannedRef.current[assignmentId], updates);
        setPlannedVersion(v => v + 1);

        // Update usage counter optimistically
        if (!wasPlanned && isNowPlanned) {
            // Assignment just got planned
            incrementUsage(1);
        } else if (wasPlanned && !isNowPlanned) {
            // Assignment just got unplanned
            decrementUsage(1);
        }

        try {
            // Then perform the actual updates asynchronously
            const result = await upsertPlannedAssignment([updates]);
            if (result?.usage) setWeeklyUsage(result.usage);
            await updatePlannedCache([updates], "update");
        } catch (error) {

            console.log("error in modifyPlanned", error);
            // The optimistic update to plannedRef.current will be reverted below

            if (Object.keys(reversionData).length > 0) {
                Object.assign(plannedRef.current[assignmentId], reversionData);
            } else {
                delete plannedRef.current[assignmentId];
            }
            setPlannedVersion(v => v + 1);

            // Revert usage counter on error
            if (!wasPlanned && isNowPlanned) {
                decrementUsage(1);
            } else if (wasPlanned && !isNowPlanned) {
                incrementUsage(1);
            }

            // Show error to user
            if (error.message.includes("gated")) {
                openGate("assignments_upsert");
            }

            return;
        }

        const { cached_planned_assignments } = getOptions("local");
        const modifiedItem = cached_planned_assignments?.[assignmentId] || {};
        const plannedCanvasId = `planned_${assignmentId}`;

        // Add to calendar sync queue
        if (modifiedItem?.plannedAt) {
            // Plan or modify - pass all event data
            await addToQueue(plannedCanvasId, 'insert', {
                title: modifiedItem.title || 'Untitled',
                startDateTime: modifiedItem.plannedAt,
                duration: modifiedItem.duration || 30,
                description: `Course: ${modifiedItem.course || 'Unknown'}\nPlanned work time`,
                type: 'planned'
            });
        } else {
            // Unplanned - delete the planned event (eventData can be minimal for delete)
            await addToQueue(plannedCanvasId, 'delete', {
                title: '',
                startDateTime: ''
            });
        }

        return true;
    }

    const updateFilters = (filter: any) => {
        setFilters((prev: any) => {
            return { ...prev, ...filter };
        });
    }


    function findClosestAvailableTime(planner: any, duration: number | null, desiredTime: any | null, customPlanned?: any, classSchedule?: any, externalEvents?: any) {

        if (!customPlanned) {
            // Prefer the latest in-memory planned map; fall back to local cache
            const localOptions = getOptions("local") || {};
            customPlanned = plannedRef.current || localOptions.cached_planned_assignments || {};
        }

        const items = customPlanned || {};
        const selectedItem = planner;

        // this should never happen, but just in case
        if (!selectedItem?.plannedAt && !desiredTime) return null;

        const currentDate = new Date(desiredTime || selectedItem.plannedAt);
        const currentDuration = duration || selectedItem?.duration || defaultTaskValues.duration;
        const currentDateStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0);

        const insertStartTime = currentDate.getHours() * 60 + currentDate.getMinutes();
        const insertEndTime = insertStartTime + currentDuration;

        // Start with existing planned assignments
        const itemsInSameDay = Object.keys(items).reduce((acc: any, itemId: any) => {
            const item = items[itemId];
            if (!item.plannedAt || itemId.toString() === selectedItem?.id?.toString()) return acc;
            const itemDate = new Date(item.plannedAt);
            if (isSameDay(itemDate, currentDateStart)) {
                const startMin = itemDate.getHours() * 60 + itemDate.getMinutes();
                acc.push({
                    "start": startMin,
                    "end": startMin + parseInt(item?.duration || "30"),
                    "type": "planned"
                });
            }
            return acc;
        }, []);

        // Add class times for the current day
        if (classSchedule) {
            const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
            const classesForDay = classSchedule[dayOfWeek] || [];
            classesForDay.forEach((classItem: any) => {
                itemsInSameDay.push({
                    "start": classItem.start,
                    "end": classItem.end,
                    "type": "class",
                    "course": classItem.course
                });
            });
        }

        // Add external calendar events for the current day
        if (externalEvents && Array.isArray(externalEvents)) {
            externalEvents.forEach((event: any) => {
                const eventStart = new Date(event.startDate || event.start?.dateTime || event.start?.date);
                const eventEnd = new Date(event.endDate || event.end?.dateTime || event.end?.date);

                // Check if event is on the same day
                if (isSameDay(eventStart, currentDateStart)) {
                    const startMin = eventStart.getHours() * 60 + eventStart.getMinutes();
                    const endMin = eventEnd.getHours() * 60 + eventEnd.getMinutes();

                    // Only add if it's not an all-day event (those don't block specific times)
                    if (!event.isAllDay && startMin < endMin) {
                        itemsInSameDay.push({
                            "start": startMin,
                            "end": endMin,
                            "type": "external",
                            "source": event.source || "calendar"
                        });
                    }
                }
            });
        }

        // Sort all items by start time
        itemsInSameDay.sort((a: any, b: any) => a.start - b.start);

        // Merge overlapping/contiguous intervals so gap detection is accurate
        const mergedIntervals: any[] = [];
        for (const interval of itemsInSameDay) {
            const last = mergedIntervals[mergedIntervals.length - 1];
            if (!last || interval.start > last.end) {
                mergedIntervals.push({ ...interval });
            } else {
                last.end = Math.max(last.end, interval.end);
            }
        }

        const insertDuration = insertEndTime - insertStartTime;

        let bestDiff = Infinity;
        let bestInsertion = null;

        for (let i = 0; i <= mergedIntervals.length; i++) {

            const prevEnd = i === 0 ? 0 : mergedIntervals[i - 1].end;
            const nextStart = i === mergedIntervals.length ? 60 * 24 : mergedIntervals[i].start;

            // inserting at the exact insert time is valid
            if (prevEnd <= insertStartTime && insertEndTime <= nextStart) {
                bestDiff = 0;
                bestInsertion = insertStartTime;
                break;
            }


            // inserting at the end of the previous item is valid (start at prevEnd)
            if (prevEnd + insertDuration <= nextStart) {
                const candidateStart = prevEnd;
                const diff = Math.abs(candidateStart - insertStartTime);
                if (diff < bestDiff) {
                    bestDiff = diff;
                    bestInsertion = candidateStart;
                }
            }

            // inserting before the next item starts is valid (end at nextStart)
            if (prevEnd <= nextStart - insertDuration) {
                const candidateStart = nextStart - insertDuration;
                const diff = Math.abs(candidateStart - insertStartTime);

                if (diff < bestDiff) {
                    bestDiff = diff;
                    bestInsertion = candidateStart;
                }
            }

        }

        if (bestInsertion === null) return null;

        currentDate.setHours(Math.floor(bestInsertion / 60), bestInsertion % 60, 0, 0);

        return {
            "plannedAt": currentDate.toISOString(),
            "duration": currentDuration,
        }
    }

    const removeLastAutomPlanFromCalendar = (lastAutoPlan: string[]) => {
        if (lastAutoPlan.length > 0) {
            const options = getOptions();
            const newPlanned = { ...options["planned"] };

            for (let itemID of lastAutoPlan) {
                delete newPlanned[itemID];
            }

            updateOptions({ "cached_planned_assignments": newPlanned }, "local");
        }
    }

    const undoAutoPlannedAssignments = async (autoPlanned: Record<string, string>) => {
        const ids = Object.keys(autoPlanned);
        if (ids.length === 0) return;

        const idsToRemove: string[] = [];
        const removedSnapshot: Record<string, Partial<AssignmentPlanned>> = {};

        for (const rawId of ids) {
            const id = rawId.toString();
            const plannedItem = plannedRef.current?.[id];
            if (!plannedItem?.plannedAt) continue;

            const expectedPlannedAt = autoPlanned[id];
            // Compare timestamps instead of strings to handle timezone/formatting differences
            const expectedTime = new Date(expectedPlannedAt).getTime();
            const actualTime = new Date(plannedItem.plannedAt).getTime();
            if (expectedPlannedAt && actualTime !== expectedTime) continue;

            const numericId = Number(id);
            removedSnapshot[id] = { id: Number.isFinite(numericId) ? numericId : (plannedItem as any).id, ...(plannedItem as any) };
            idsToRemove.push(id);
        }

        if (idsToRemove.length === 0) return;

        for (const id of idsToRemove) {
            delete plannedRef.current[id];
        }
        setPlannedVersion(v => v + 1);

        try {
            const deletePayload = idsToRemove.map((id) => ({ id: Number(id) }));
            await updatePlannedCache(deletePayload, "delete");
            const deleteResult = await deletePlannedAssignments(idsToRemove, { showError: false });
            if (deleteResult === null) {
                throw new Error("Failed to delete assignments from backend");
            }
        } catch (error) {
            // Revert optimistic updates if deletion fails
            for (const [id, item] of Object.entries(removedSnapshot)) {
                plannedRef.current[id] = item as any;
            }
            setPlannedVersion(v => v + 1);

            await updatePlannedCache(Object.values(removedSnapshot), "update");
            showError("Failed to undo auto-planned assignments. Changes have been reverted.", "error");
        }
    };

    const removeFromCalendar = async (item: AssignmentPlanned) => {
        try {
            // Directly delete from ref to avoid spreading
            delete plannedRef.current[item.id];

            // Trigger re-render
            setPlannedVersion(v => v + 1);

            // Then perform the actual updates asynchronously
            // 1. Update the local cache to reflect the deletion
            await updatePlannedCache([{ id: item.id }], "delete");

            // 2. Delete from backend database - backend returns updated usage
            const deleteResult = await deletePlannedAssignments([item.id], { showError: false });
            if (deleteResult?.usage) setWeeklyUsage(deleteResult.usage);

            // 3. Remove from external calendars (Google/Microsoft) - use new queue system
            const plannedCanvasId = `planned_${item.id}`;
            await addToQueue(plannedCanvasId, 'delete', {
                title: '',
                startDateTime: ''
            });


        } catch (error) {
            console.error('Error removing assignment from calendar:', error);
            // On error, the useEffect will restore the correct state when plannedRaw updates
        }
    }

    async function autoScheduleTasks(unplanned: any[]): Promise<Record<string, string>> {
        if (!unplanned || unplanned.length === 0) return {};

        // Wait for any in-progress scheduling to complete first (prevents race conditions)
        if (schedulingInProgressRef.current) {
            await schedulingInProgressRef.current;
        }

        const schedulePromise = (async (): Promise<Record<string, string>> => {
        // Start from the latest planned map (prefer in-memory ref, fallback to cached local storage)
        const cachedPlanned = getOptions("local")?.["cached_planned_assignments"] || {};
        const planned = { ...cachedPlanned, ...(plannedRef.current || {}) };
        const autoPlanDaysRaw = settings?.assignments_auto_plan?.days;
        const autoPlanTimeRaw = settings?.assignments_auto_plan?.time;
        const days = typeof autoPlanDaysRaw === "number" && Number.isFinite(autoPlanDaysRaw) ? autoPlanDaysRaw : 0;
        const autoPlanned: Record<string, string> = {};
        const timingPreferences: { [key: string]: number } = {
            "morning": 10,    // 9 AM
            "afternoon": 14,   // 12 PM
            "evening": 18,   // 6 PM  
            "night": 22,
        };
        const normalizeTimePreference = (value: unknown): number => {
            if (typeof value === "number" && Number.isFinite(value)) {
                return Math.max(0, Math.min(23, Math.round(value)));
            }
            if (typeof value === "string") {
                const normalized = value.trim().toLowerCase();
                const mapped = timingPreferences[normalized];
                if (typeof mapped === "number") return mapped;
                const parsed = Number.parseInt(normalized, 10);
                if (Number.isFinite(parsed)) return Math.max(0, Math.min(23, parsed));
            }
            return timingPreferences["morning"];
        };
        const recommendedHour = normalizeTimePreference(autoPlanTimeRaw);


        const newPlanned = { ...planned };

        let totalMessages = 0;

        const output: any = {};

        const today = new Date();
        for (const task of unplanned) {
            const diff = Math.max(0, differenceInCalendarDays(task.current.due, today) - days);
            const currentTime = new Date(today.getFullYear(), today.getMonth(), today.getDate() + diff, recommendedHour);
            const closestTime = await findClosestAvailableTime(task.current, null, currentTime, newPlanned, classSchedule, calendarEvents);
            if (!closestTime) {
                showError("Couldn't find space to fit this task for this day", "error");
                continue;
            }

            newPlanned[task.current.id] = {
                "course": task.current.course,
                "title": task.current.title,
                ...closestTime,
            }


            totalMessages++;

            output[task.current.id] = {
                "id": task.current.id,
                "course": task.current.course,
                "title": task.current.title,
                ...closestTime,
            }
            autoPlanned[String(task.current.id)] = closestTime.plannedAt;
        }

        if (Object.keys(output).length > 0) {
            console.log("new planned here", Object.values(output), Object.values(output).map((item: any) => mapAssignmentToApi(item)));

            // Directly mutate ref for auto-scheduled tasks
            for (const [id, updates] of Object.entries(output)) {
                if (!plannedRef.current[id]) {
                    plannedRef.current[id] = {};
                }
                Object.assign(plannedRef.current[id], updates);
            }

            // Trigger re-render
            setPlannedVersion(v => v + 1);

            try {
                // Then perform the actual updates asynchronously
                const result = await upsertPlannedAssignment(Object.values(output));
                await updatePlannedCache(Object.values(output), "update");

                // Update usage from the backend response
                if (planType !== 'pro' && result?.usage) {
                    setWeeklyUsage(result.usage);
                }

                // Show consolidated success toast grouped by date
                const plannedByDate: Record<string, number> = {};
                for (const task of unplanned) {
                    const plannedInfo = newPlanned[task.current.id];
                    if (plannedInfo?.plannedAt) {
                        const date = new Date(plannedInfo.plannedAt);
                        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                        plannedByDate[dayName] = (plannedByDate[dayName] || 0) + 1;
                    }
                }

                const summaryParts = Object.entries(plannedByDate).map(
                    ([day, count]) => `${count} on ${day}`
                );
                if (summaryParts.length > 0) {
                    showError(`Scheduled: ${summaryParts.join(', ')}`, "success");
                }
            } catch (error: any) {
                // Reload from local storage cache to revert optimistic update
                const localCache = getOptions("local")["cached_planned_assignments"] || {};
                plannedRef.current = localCache;
                setPlannedVersion(v => v + 1);

                // Show error to user
                if (error.message === "gated") {
                    openGate("assignments_upsert");
                } else {
                    showError("Failed to schedule assignments. Changes have been reverted.", "error");
                }
                return {};
            }
        }

        if (totalMessages === 0) return {};

        // Trigger calendar sync for all newly scheduled items
        if (totalMessages > 0) {
            console.log('[PlannerContext] Triggering calendar sync for', totalMessages, 'auto-scheduled items');

            setTimeout(async () => {
                try {
                    for (const task of unplanned) {
                        const assignmentData = {
                            ...task.current,
                            ...newPlanned[task.current.id]
                        };

                        // Add to calendar sync queue using new system
                        const plannedCanvasId = `planned_${task.current.id}`;
                        await addToQueue(plannedCanvasId, 'insert', {
                            title: assignmentData.title || 'Untitled',
                            startDateTime: assignmentData.plannedAt,
                            duration: assignmentData.duration || 30,
                            description: `Course: ${assignmentData.course || 'Unknown'}\nPlanned work time`,
                            type: 'planned'
                        });
                    }
                } catch (error) {
                    console.error('[PlannerContext] Error triggering calendar sync for auto-scheduled items:', error);
                }
            }, 200); // Delay to ensure storage is updated
        }

        /*
        setScrollToHour(Math.min(...Object.keys(newPlanned).map((key: string) => {
            return new Date(newPlanned[key].plannedAt).getHours();
        })));
        */

        return autoPlanned;
        })();

        schedulingInProgressRef.current = schedulePromise;
        try {
            return await schedulePromise;
        } finally {
            schedulingInProgressRef.current = null;
        }
    }

    const combineAllItems = () => {

        const defaultOverrides: any = {
            "plannedAt": null,
            "duration": 30,
            "status": "incomplete",
            "points": null,
            "is_custom": false,
            "url": null,
            "priority": null,
        }

        const items = apiCache.concat(customAssignments).map((item: any) => {
            const itemOverrides = overrides?.[item.id] || {};
            const itemPlanned = planned?.[item.id] || {};

            const original = {
                ...defaultOverrides,
                ...item,
            }

            return {
                "original": original,
                "current": {
                    ...original,
                    ...itemPlanned,
                    ...itemOverrides,
                    "numLinks": links?.[LinkType.ASSIGNMENT]?.[item.id]?.length || 0,
                }
            }
        });

        // Ensure we only keep one entry per assignment id to avoid duplicate due items
        const deduped = Array.from(
            items.reduce((acc: Map<string, any>, item: any) => {
                const rawId = item?.current?.id ?? item?.original?.id;
                if (rawId === undefined || rawId === null) return acc;
                const key = String(rawId);
                if (!acc.has(key)) acc.set(key, item);
                return acc;
            }, new Map<string, any>()).values()
        );

        const filtered = Object.keys(filters).some((key) => filters[key].length > 0) ? filterItems(deduped) : deduped;
        setDueItems(filtered);

    }

    useEffect(() => {
        combineAllItems();
    }, [customAssignments, filters, overrides, apiCache, plannedVersion, links, todoCacheVersion]);


    const value = {
        startDate,
        setStartDate,
        dueItems,
        setDueItems,
        scrollPos,
        scrollArea,
        calendarActions,
        view,
        setView,
        miniCalStart,
        setMiniCalStart,
        modifyOverrides,
        filters,
        updateFilters,
        miniCalDate,
        setMiniCalDate,
        settings,
        updateSettings,
        scrollToHour,
        setScrollToHour,
        numCols,
        setNumCols,
        findClosestAvailableTime: (planner: any, duration: number | null, desiredTime: any | null, customPlanned?: any) => {
            return findClosestAvailableTime(planner, duration, desiredTime, customPlanned, classSchedule, calendarEvents);
        },
        blockHeight,
        blockWidth,
        timeWidth,
        blockMargin,
        subBlocks,
        subBlockMinutes,
        subBlockHeight,
        getRowFromDelta,
        dueBlockWidth,
        setWeekDays,
        setZoom,
        unassignedStart,
        setUnassignedStart,
        autoScheduleTasks,
        overrides,
        cursorType,
        setCursorType,
        loading,
        modifyPlanned,
        planned,
        portal,
        creatorAssignment,
        setCreatorAssignment,
        removeFromCalendar,
        removeLastAutomPlanFromCalendar,
        undoAutoPlannedAssignments,
        updateCoverage,
        weeklyUsage,
        usageLoading,
        /*
        schedule,
        courseData,
        */
    }

    return <PlannerContext.Provider value={value}>{children}</PlannerContext.Provider>;
}
